// 物理正确的光强叠加函数
const addLightIntensity = (
  existingR,
  existingG,
  existingB,
  newR,
  newG,
  newB,
  brightness
) => {
  // 调整亮度系数，让演示效果更清晰
  const factor = brightness * 220; // 从180增加到220，提高基础亮度

  // 将RGB转换为亮度，叠加后再转回RGB
  const newIntensityR = (newR / 255) * factor;
  const newIntensityG = (newG / 255) * factor;
  const newIntensityB = (newB / 255) * factor;

  // 使用更温和的叠加模型（线性叠加+平方根压缩）
  const combinedR = Math.sqrt(
    existingR * existingR + newIntensityR * newIntensityR * 0.8
  );
  const combinedG = Math.sqrt(
    existingG * existingG + newIntensityG * newIntensityG * 0.8
  );
  const combinedB = Math.sqrt(
    existingB * existingB + newIntensityB * newIntensityB * 0.8
  );

  return {
    r: Math.min(255, combinedR),
    g: Math.min(255, combinedG),
    b: Math.min(255, combinedB),
  };
};

// 在画布上绘制不同形状光斑的函数
const drawLightSpot = (
  imageData,
  centerX,
  centerY,
  spotSize,
  color,
  brightness,
  shape
) => {
  const data = imageData.data;
  const canvasWidth = imageData.width;
  const canvasHeight = imageData.height;

  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);

  const radius = Math.max(spotSize / 2, 0.5);

  // 统一的边缘衰减函数 - 较为锐利，符合几何光学
  const getEdgeFactor = (distanceFromEdge) => {
    if (distanceFromEdge <= 0) return 1.0; // 完全在内部
    if (distanceFromEdge > 2) return 0; // 超出软边缘
    return Math.max(0, 1 - distanceFromEdge * 0.3); // 轻微的线性衰减
  };

  // 根据小孔形状绘制不同形状的光斑
  if (shape === "circle") {
    // 圆形光斑
    for (let dy = -Math.ceil(radius + 2); dy <= Math.ceil(radius + 2); dy++) {
      for (let dx = -Math.ceil(radius + 2); dx <= Math.ceil(radius + 2); dx++) {
        const distance = Math.sqrt(dx * dx + dy * dy);
        const distanceFromEdge = distance - radius;

        if (distanceFromEdge <= 2) {
          // 包含软边缘
          const px = Math.round(centerX + dx);
          const py = Math.round(centerY + dy);

          if (px >= 0 && px < canvasWidth && py >= 0 && py < canvasHeight) {
            const index = (py * canvasWidth + px) * 4;
            const edgeFactor = getEdgeFactor(distanceFromEdge);
            const effectiveBrightness = brightness * edgeFactor;

            if (effectiveBrightness > 0) {
              const existing = {
                r: data[index],
                g: data[index + 1],
                b: data[index + 2],
              };
              const combined = addLightIntensity(
                existing.r,
                existing.g,
                existing.b,
                r,
                g,
                b,
                effectiveBrightness
              );

              data[index] = combined.r;
              data[index + 1] = combined.g;
              data[index + 2] = combined.b;
              data[index + 3] = 255;
            }
          }
        }
      }
    }
  } else if (shape === "square") {
    // 方形光斑 - 修复对角线问题
    for (let dy = -Math.ceil(radius + 2); dy <= Math.ceil(radius + 2); dy++) {
      for (let dx = -Math.ceil(radius + 2); dx <= Math.ceil(radius + 2); dx++) {
        // 计算到正方形边缘的最短距离
        const distToLeft = radius + dx;
        const distToRight = radius - dx;
        const distToTop = radius + dy;
        const distToBottom = radius - dy;

        // 如果在正方形内部，距离为负；在外部，距离为正
        const distanceFromEdge = -Math.min(
          distToLeft,
          distToRight,
          distToTop,
          distToBottom
        );

        if (distanceFromEdge <= 2) {
          // 包含软边缘
          const px = Math.round(centerX + dx);
          const py = Math.round(centerY + dy);

          if (px >= 0 && px < canvasWidth && py >= 0 && py < canvasHeight) {
            const index = (py * canvasWidth + px) * 4;
            const edgeFactor = getEdgeFactor(distanceFromEdge);
            const effectiveBrightness = brightness * edgeFactor;

            if (effectiveBrightness > 0) {
              const existing = {
                r: data[index],
                g: data[index + 1],
                b: data[index + 2],
              };
              const combined = addLightIntensity(
                existing.r,
                existing.g,
                existing.b,
                r,
                g,
                b,
                effectiveBrightness
              );

              data[index] = combined.r;
              data[index + 1] = combined.g;
              data[index + 2] = combined.b;
              data[index + 3] = 255;
            }
          }
        }
      }
    }
  } else if (shape === "triangle") {
    // 三角形光斑 - 计算到三条边的真实距离
    for (let dy = -Math.ceil(radius + 2); dy <= Math.ceil(radius + 2); dy++) {
      for (let dx = -Math.ceil(radius + 2); dx <= Math.ceil(radius + 2); dx++) {
        const x = dx;
        const y = dy;

        // 等边三角形的三个顶点
        const top_y = -radius * 0.8;
        const bottom_y = radius * 0.4;
        const half_width = radius * 0.7;

        // 三角形的三个顶点
        const vertices = [
          [0, top_y], // 顶点
          [-half_width, bottom_y], // 左下
          [half_width, bottom_y], // 右下
        ];

        // 计算点到三角形的距离
        let distanceFromEdge = Infinity;

        // 检查是否在三角形内部
        let inside = true;

        // 检查三条边：
        // 1. 左边：从顶点到左下
        // 2. 右边：从顶点到右下
        // 3. 底边：从左下到右下

        // 左边 (顶点到左下)
        const leftEdgeX = (-half_width * (y - top_y)) / (bottom_y - top_y);
        if (x < leftEdgeX) inside = false;

        // 右边 (顶点到右下)
        const rightEdgeX = (half_width * (y - top_y)) / (bottom_y - top_y);
        if (x > rightEdgeX) inside = false;

        // 底边
        if (y > bottom_y) inside = false;
        if (y < top_y) inside = false;

        if (inside) {
          // 在三角形内部，计算到最近边缘的距离（负值）
          const distToLeft = x - leftEdgeX;
          const distToRight = rightEdgeX - x;
          const distToBottom = bottom_y - y;

          // 顶部需要特殊处理，因为是尖角
          let distToTop;
          if (y < top_y + (bottom_y - top_y) * 0.1) {
            // 接近顶点时，计算到顶点的距离
            distToTop = Math.sqrt(x * x + (y - top_y) * (y - top_y));
          } else {
            distToTop = Infinity; // 远离顶点时不考虑
          }

          distanceFromEdge = -Math.min(
            distToLeft,
            distToRight,
            distToBottom,
            distToTop
          );
        } else {
          // 在三角形外部，计算到三角形的最短距离

          // 计算到三条边的距离
          const distances = [];

          // 到左边的距离
          if (y >= top_y && y <= bottom_y) {
            const leftX = (-half_width * (y - top_y)) / (bottom_y - top_y);
            if (x < leftX) {
              distances.push(leftX - x);
            }
          }

          // 到右边的距离
          if (y >= top_y && y <= bottom_y) {
            const rightX = (half_width * (y - top_y)) / (bottom_y - top_y);
            if (x > rightX) {
              distances.push(x - rightX);
            }
          }

          // 到底边的距离
          if (y > bottom_y && x >= -half_width && x <= half_width) {
            distances.push(y - bottom_y);
          }

          // 到顶点的距离
          if (y < top_y) {
            distances.push(Math.sqrt(x * x + (y - top_y) * (y - top_y)));
          }

          // 到三个角点的距离
          distances.push(Math.sqrt(x * x + (y - top_y) * (y - top_y))); // 顶点
          distances.push(
            Math.sqrt(
              (x + half_width) * (x + half_width) +
                (y - bottom_y) * (y - bottom_y)
            )
          ); // 左下
          distances.push(
            Math.sqrt(
              (x - half_width) * (x - half_width) +
                (y - bottom_y) * (y - bottom_y)
            )
          ); // 右下

          distanceFromEdge = Math.min(...distances);
        }

        if (distanceFromEdge <= 2) {
          // 包含软边缘
          const px = Math.round(centerX + dx);
          const py = Math.round(centerY + dy);

          if (px >= 0 && px < canvasWidth && py >= 0 && py < canvasHeight) {
            const index = (py * canvasWidth + px) * 4;
            const edgeFactor = getEdgeFactor(distanceFromEdge);
            const effectiveBrightness = brightness * edgeFactor;

            if (effectiveBrightness > 0) {
              const existing = {
                r: data[index],
                g: data[index + 1],
                b: data[index + 2],
              };
              const combined = addLightIntensity(
                existing.r,
                existing.g,
                existing.b,
                r,
                g,
                b,
                effectiveBrightness
              );

              data[index] = combined.r;
              data[index + 1] = combined.g;
              data[index + 2] = combined.b;
              data[index + 3] = 255;
            }
          }
        }
      }
    }
  }
};

import React, { useState, useEffect, useRef } from "react";

const PinholeCameraDemo = () => {
  const [apertureSize, setApertureSize] = useState([15]);
  const [apertureShape, setApertureShape] = useState("circle"); // 新增：小孔形状
  const [observationDirection, setObservationDirection] =
    useState("right-to-left"); // 新增：观察方向
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [showCompleteImage, setShowCompleteImage] = useState(false);
  const [showFeaturePoints, setShowFeaturePoints] = useState(true);
  const [brightnessMultiplier, setBrightnessMultiplier] = useState([7]); // 从5提高到7，默认更亮
  const screenViewRef = useRef(null);

  // 彩色字母F图案
  const letterFImage = {
    width: 80,
    height: 100,
    getColorAt: (x, y) => {
      const strokeWidth = 8;

      // 左侧竖线 (红色)
      if (x >= 10 && x < 10 + strokeWidth) {
        if (y >= 10 && y < 90) {
          return "#FF1744"; // 亮红色
        }
      }

      // 顶部横线 (蓝色)
      if (y >= 10 && y < 10 + strokeWidth) {
        if (x >= 10 && x < 65) {
          return "#2196F3"; // 亮蓝色
        }
      }

      // 中间横线 (绿色)
      if (y >= 40 && y < 40 + strokeWidth) {
        if (x >= 10 && x < 50) {
          return "#4CAF50"; // 亮绿色
        }
      }

      // 添加几个小装饰点便于观察
      // 右上角点 (黄色) - 确保位置正确
      if (x >= 58 && x < 68 && y >= 12 && y < 22) {
        return "#FFD700"; // 金黄色
      }

      // 中间右侧点 (紫色)
      if (x >= 45 && x < 50 && y >= 43 && y < 48) {
        return "#9C27B0"; // 紫色
      }

      // 底部点 (橙色)
      if (x >= 12 && x < 17 && y >= 80 && y < 85) {
        return "#FF9800"; // 橙色
      }

      return null; // 透明背景
    },
  };

  // SVG 尺寸和位置
  const svgWidth = 900;
  const svgHeight = 500;
  const sourceX = 150;
  const apertureX = 400;
  const screenX = 700;

  // 正确计算通过小孔的成像位置（确保倒立）
  const calculateImagePosition = (objectX, objectY) => {
    const objectDistanceFromCenter = {
      x: objectX - sourceX,
      y: objectY - 250,
    };

    const objectDistance = apertureX - sourceX;
    const imageDistance = screenX - apertureX;

    // 倒立成像
    const imageX =
      screenX - (objectDistanceFromCenter.x * imageDistance) / objectDistance;
    const imageY =
      250 - (objectDistanceFromCenter.y * imageDistance) / objectDistance;

    return { x: imageX, y: imageY };
  };

  // 计算物理正确的光斑参数 - 基于边界光线的真实计算
  const calculateLightingParams = (pointX = sourceX, pointY = 250) => {
    const aperture = apertureSize[0];

    // 计算通过小孔上下边界的光线在光屏上的位置
    const topEdgeY = 250 - aperture / 2;
    const bottomEdgeY = 250 + aperture / 2;

    const topRaySlope = (topEdgeY - pointY) / (apertureX - pointX);
    const bottomRaySlope = (bottomEdgeY - pointY) / (apertureX - pointX);

    const topRayEndY = topEdgeY + topRaySlope * (screenX - apertureX);
    const bottomRayEndY = bottomEdgeY + bottomRaySlope * (screenX - apertureX);

    // 真实的光斑大小 = 边界光线在光屏上的距离
    const spotSize = Math.abs(bottomRayEndY - topRayEndY);

    // 光通量：正比于小孔面积
    const lightFlux = aperture * aperture; // 光通量 ∝ 孔面积

    // 单个光斑的平均亮度：光通量/光斑面积
    const spotArea = Math.PI * (spotSize / 2) * (spotSize / 2);
    const avgBrightness = lightFlux / Math.max(spotArea, 1); // 防止除零

    // 应用用户亮度调节 - 提高基础亮度，并增加低端敏感性
    const brightnessFactor = brightnessMultiplier[0];
    // 使用平方函数让低端更敏感
    const sensitivityAdjusted =
      brightnessFactor < 2
        ? brightnessFactor * brightnessFactor * 0.5
        : brightnessFactor;
    const normalizedBrightness =
      Math.min(avgBrightness / 15, 1) * sensitivityAdjusted;

    return {
      spotSize,
      lightFlux,
      brightness: Math.max(normalizedBrightness, 0.02), // 从0.15降到0.02，允许很暗的效果
      topRayEndY,
      bottomRayEndY,
    };
  };

  // 添加或移除选择点
  const togglePoint = (x, y) => {
    const localX = x - (sourceX - letterFImage.width / 2);
    const localY = y - (250 - letterFImage.height / 2);

    if (
      localX < 0 ||
      localX >= letterFImage.width ||
      localY < 0 ||
      localY >= letterFImage.height
    ) {
      return;
    }

    const color = letterFImage.getColorAt(localX, localY);
    if (!color) return;

    const newPoint = { x, y, color, id: Date.now() };
    const existingIndex = selectedPoints.findIndex(
      (p) => Math.abs(p.x - x) < 8 && Math.abs(p.y - y) < 8
    );

    if (existingIndex >= 0) {
      setSelectedPoints((prev) => prev.filter((_, i) => i !== existingIndex));
    } else {
      setSelectedPoints((prev) => [...prev, newPoint]);
    }
  };

  // 生成物理正确的光屏成像效果
  const generateScreenView = () => {
    const canvas = screenViewRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const canvasWidth = 300;
    const canvasHeight = 400;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // 创建ImageData用于像素级操作
    const imageData = ctx.createImageData(canvasWidth, canvasHeight);

    // 如果显示完整图像 - 处理所有像素点
    if (showCompleteImage) {
      for (let y = 0; y < letterFImage.height; y += 2) {
        // 跳帧采样提高性能
        for (let x = 0; x < letterFImage.width; x += 2) {
          const color = letterFImage.getColorAt(x, y);
          if (!color) continue;

          const objectX = sourceX - letterFImage.width / 2 + x;
          const objectY = 250 - letterFImage.height / 2 + y;
          const imagePos = calculateImagePosition(objectX, objectY);

          // 获取该点的真实光斑参数
          const { spotSize, brightness } = calculateLightingParams(
            objectX,
            objectY
          );

          // 转换到画布坐标
          let canvasX = ((imagePos.x - screenX + 150) * canvasWidth) / 300;
          const canvasY = ((imagePos.y - 100) * canvasHeight) / 400;

          // 根据观察方向调整X坐标
          if (observationDirection === "left-to-right") {
            canvasX = canvasWidth - canvasX;
          }

          if (
            canvasX >= 0 &&
            canvasX < canvasWidth &&
            canvasY >= 0 &&
            canvasY < canvasHeight
          ) {
            // 完整图像使用适中的亮度，低亮度时进一步降低
            const imageMultiplier = brightnessMultiplier[0] < 1 ? 0.6 : 1.2;
            drawLightSpot(
              imageData,
              canvasX,
              canvasY,
              spotSize,
              color,
              brightness * imageMultiplier,
              apertureShape
            );
          }
        }
      }
    }

    // 如果显示特征点 - 叠加在完整图像上
    if (showFeaturePoints) {
      selectedPoints.forEach((point) => {
        const imagePos = calculateImagePosition(point.x, point.y);
        const { spotSize, brightness } = calculateLightingParams(
          point.x,
          point.y
        );

        let canvasX = ((imagePos.x - screenX + 150) * canvasWidth) / 300;
        const canvasY = ((imagePos.y - 100) * canvasHeight) / 400;

        // 根据观察方向调整X坐标
        if (observationDirection === "left-to-right") {
          canvasX = canvasWidth - canvasX;
        }

        if (
          canvasX >= 0 &&
          canvasX < canvasWidth &&
          canvasY >= 0 &&
          canvasY < canvasHeight
        ) {
          // 特征点使用更高的亮度，便于观察，低亮度时也相应降低
          const featureMultiplier = brightnessMultiplier[0] < 1 ? 2.0 : 4.5;
          drawLightSpot(
            imageData,
            canvasX,
            canvasY,
            spotSize,
            point.color,
            brightness * featureMultiplier,
            apertureShape
          );
        }
      });
    }

    // 应用合成的图像数据
    ctx.putImageData(imageData, 0, 0);

    // 极大孔径时的额外弥散光效果
    if (apertureSize[0] > 50 && showCompleteImage) {
      ctx.fillStyle = `rgba(255, 255, 255, 0.1)`;
      ctx.beginPath();
      ctx.arc(
        canvasWidth / 2,
        canvasHeight / 2,
        apertureSize[0] / 4,
        0,
        Math.PI * 2
      );
      ctx.fill();
    }
  };

  useEffect(() => {
    generateScreenView();
  }, [
    apertureSize,
    apertureShape,
    observationDirection,
    selectedPoints,
    showCompleteImage,
    showFeaturePoints,
    brightnessMultiplier,
  ]);

  return (
    <div className="w-full max-w-7xl mx-auto p-6 bg-gray-50 rounded-lg">
      <h1 className="text-3xl font-bold text-center mb-6 text-gray-800">
        📝 小孔成像字母F模拟器 - by陈硕老师（增强观察视角版）
      </h1>

      {/* 控制面板 */}
      <div className="mb-6 space-y-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-xs text-gray-500">
            💡 点击字母F上的彩色部位来选择特征点，观察它们在光屏上的倒立成像。
            现在可以选择观察视角了！
          </div>
        </div>
      </div>

      {/* 主要演示区域 */}
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* 光路图 */}
          <div className="flex-1">
            <h4 className="text-md font-semibold mb-4">光路追踪图</h4>

            {/* 小孔参数控制 */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    小孔直径: {apertureSize[0]}px
                  </label>
                  <input
                    type="range"
                    min="2"
                    max="80"
                    step="2"
                    value={apertureSize[0]}
                    onChange={(e) =>
                      setApertureSize([parseInt(e.target.value)])
                    }
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium text-gray-700">
                    小孔形状:
                  </label>
                  <select
                    value={apertureShape}
                    onChange={(e) => setApertureShape(e.target.value)}
                    className="p-2 border border-gray-300 rounded-md bg-white text-sm"
                  >
                    <option value="circle">⭕ 圆形</option>
                    <option value="square">⬜ 方形</option>
                    <option value="triangle">🔺 三角形</option>
                  </select>
                </div>

                {/* 新增：观察视角选择 */}
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium text-gray-700">
                    观察视角:
                  </label>
                  <select
                    value={observationDirection}
                    onChange={(e) => setObservationDirection(e.target.value)}
                    className="p-2 border border-gray-300 rounded-md bg-white text-sm"
                  >
                    <option value="right-to-left">👁️← 从右往左看</option>
                    <option value="left-to-right">→👁️ 从左往右看</option>
                  </select>
                </div>
              </div>

              {/* 控制按钮移到这里 */}
              <div className="mt-3 flex flex-wrap gap-3 items-center">
                <button
                  onClick={() => setSelectedPoints([])}
                  className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 transition-colors"
                >
                  🗑️ 清除所有点
                </button>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showCompleteImage}
                    onChange={(e) => setShowCompleteImage(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-xs">🖼️ 显示完整图像</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showFeaturePoints}
                    onChange={(e) => setShowFeaturePoints(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-xs">🎯 显示特征点</span>
                </label>

                <span className="text-xs text-gray-600">
                  已选择 {selectedPoints.length} 个点
                </span>
              </div>

              <div className="mt-2 text-xs text-gray-600">
                💡 小孔形状决定光斑形状 - 这是真实的光学现象！
                <span className="ml-4 font-medium">当前效果: </span>
                {apertureSize[0] < 10
                  ? "🔍 高清晰度，中等亮度"
                  : apertureSize[0] < 30
                  ? "⚖️ 平衡清晰度和亮度"
                  : apertureSize[0] < 50
                  ? "💡 高亮度，较模糊"
                  : "🌟 超亮，严重模糊"}
              </div>

              {/* 新增：观察视角说明 */}
              <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                <strong>🔄 观察视角效果：</strong>
                {observationDirection === "right-to-left" ? (
                  <span>从右往左看 - 看到完全倒立的像（上下+左右颠倒）</span>
                ) : (
                  <span>
                    从左往右看 - 由于观察者视角旋转180°，只有上下颠倒！
                  </span>
                )}
              </div>
            </div>

            <svg
              width={svgWidth}
              height={svgHeight}
              viewBox={`0 0 ${svgWidth} ${svgHeight}`}
              className="w-full border border-gray-300 rounded"
            >
              {/* 背景 */}
              <rect width="100%" height="100%" fill="#f8f9fa" />

              {/* 坐标网格 */}
              <defs>
                <pattern
                  id="grid"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 20 0 L 0 0 0 20"
                    fill="none"
                    stroke="#e0e0e0"
                    strokeWidth="0.5"
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />

              {/* 绘制字母F图像 */}
              <g>
                {Array.from({ length: letterFImage.height }, (_, y) =>
                  Array.from({ length: letterFImage.width }, (_, x) => {
                    const color = letterFImage.getColorAt(x, y);
                    if (!color) return null;

                    const pixelX = sourceX - letterFImage.width / 2 + x;
                    const pixelY = 250 - letterFImage.height / 2 + y;

                    return (
                      <rect
                        key={`pixel-${x}-${y}`}
                        x={pixelX}
                        y={pixelY}
                        width="1"
                        height="1"
                        fill={color}
                        className="cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => togglePoint(pixelX, pixelY)}
                      />
                    );
                  })
                )
                  .flat()
                  .filter(Boolean)}
              </g>

              {/* 小孔装置 - 侧视图，只显示缝隙 */}
              <g>
                <rect
                  x={apertureX - 15}
                  y={100}
                  width={30}
                  height={150 - apertureSize[0] / 2}
                  fill="#654321"
                />
                <rect
                  x={apertureX - 15}
                  y={250 + apertureSize[0] / 2}
                  width={30}
                  height={150 - apertureSize[0] / 2}
                  fill="#654321"
                />

                {/* 小孔（侧视图只显示缝隙，不显示具体形状） */}
                <rect
                  x={apertureX - 15}
                  y={250 - apertureSize[0] / 2}
                  width={30}
                  height={apertureSize[0]}
                  fill="#000"
                />

                {/* 小孔边缘标记 */}
                <circle
                  cx={apertureX - 15}
                  cy={250 - apertureSize[0] / 2}
                  r="2"
                  fill="#FF0000"
                />
                <circle
                  cx={apertureX - 15}
                  cy={250 + apertureSize[0] / 2}
                  r="2"
                  fill="#FF0000"
                />
              </g>

              {/* 光屏 */}
              <rect
                x={screenX - 5}
                y={100}
                width={10}
                height={400}
                fill="#F5F5F5"
                stroke="#666"
                strokeWidth="3"
              />

              {/* 新增：观察者图标 */}
              {observationDirection === "right-to-left" ? (
                <text
                  x={screenX + 30}
                  y={260}
                  fontSize="24"
                  textAnchor="start"
                  fill="#2196F3"
                >
                  👁️
                </text>
              ) : (
                <text
                  x={screenX - 30}
                  y={260}
                  fontSize="24"
                  textAnchor="end"
                  fill="#FF9800"
                >
                  👁️
                </text>
              )}

              {/* 绘制选中点的光线 */}
              {selectedPoints.map((point) => {
                const imagePos = calculateImagePosition(point.x, point.y);
                const lightingParams = calculateLightingParams(
                  point.x,
                  point.y
                );
                const { spotSize, topRayEndY, bottomRayEndY } = lightingParams;

                const topEdgeY = 250 - apertureSize[0] / 2;
                const bottomEdgeY = 250 + apertureSize[0] / 2;

                return (
                  <g key={point.id}>
                    {/* 主光线 */}
                    <line
                      x1={point.x}
                      y1={point.y}
                      x2={apertureX}
                      y2={250}
                      stroke={point.color}
                      strokeWidth="3"
                      opacity="0.8"
                    />
                    <line
                      x1={apertureX}
                      y1={250}
                      x2={screenX}
                      y2={imagePos.y}
                      stroke={point.color}
                      strokeWidth="3"
                      opacity="0.8"
                    />

                    {/* 边缘光线 */}
                    <line
                      x1={point.x}
                      y1={point.y}
                      x2={apertureX}
                      y2={topEdgeY}
                      stroke={point.color}
                      strokeWidth="2"
                      opacity="0.5"
                      strokeDasharray="4,4"
                    />
                    <line
                      x1={apertureX}
                      y1={topEdgeY}
                      x2={screenX}
                      y2={topRayEndY}
                      stroke={point.color}
                      strokeWidth="2"
                      opacity="0.5"
                      strokeDasharray="4,4"
                    />

                    <line
                      x1={point.x}
                      y1={point.y}
                      x2={apertureX}
                      y2={bottomEdgeY}
                      stroke={point.color}
                      strokeWidth="2"
                      opacity="0.5"
                      strokeDasharray="4,4"
                    />
                    <line
                      x1={apertureX}
                      y1={bottomEdgeY}
                      x2={screenX}
                      y2={bottomRayEndY}
                      stroke={point.color}
                      strokeWidth="2"
                      opacity="0.5"
                      strokeDasharray="4,4"
                    />

                    {/* 光屏上的光斑 - 现在大小与边界光线一致 */}
                    {apertureShape === "circle" && (
                      <ellipse
                        cx={screenX}
                        cy={imagePos.y}
                        rx="4"
                        ry={Math.max(spotSize / 2, 2)}
                        fill={point.color}
                        opacity="0.8"
                      />
                    )}

                    {apertureShape === "square" && (
                      <rect
                        x={screenX - 3}
                        y={imagePos.y - Math.max(spotSize / 2, 2)}
                        width="6"
                        height={Math.max(spotSize, 4)}
                        fill={point.color}
                        opacity="0.8"
                      />
                    )}

                    {apertureShape === "triangle" && (
                      <polygon
                        points={`${screenX},${
                          imagePos.y - Math.max(spotSize / 2, 2)
                        } ${screenX - 2},${
                          imagePos.y + Math.max(spotSize / 2, 2)
                        } ${screenX + 2},${
                          imagePos.y + Math.max(spotSize / 2, 2)
                        }`}
                        fill={point.color}
                        opacity="0.8"
                      />
                    )}

                    {/* 选中点标记 */}
                    <circle
                      cx={point.x}
                      cy={point.y}
                      r="5"
                      fill="white"
                      stroke={point.color}
                      strokeWidth="3"
                    />
                  </g>
                );
              })}

              {/* 标签 */}
              <text
                x={sourceX}
                y={80}
                textAnchor="middle"
                className="text-lg font-bold"
              >
                📝 字母F
              </text>
              <text
                x={apertureX}
                y={80}
                textAnchor="middle"
                className="text-lg font-bold"
              >
                🕳️{" "}
                {apertureShape === "circle"
                  ? "圆形"
                  : apertureShape === "square"
                  ? "方形"
                  : "三角形"}
                小孔
              </text>
              <text
                x={screenX}
                y={80}
                textAnchor="middle"
                className="text-lg font-bold"
              >
                📺 光屏
              </text>
            </svg>
          </div>

          {/* 光屏成像效果 */}
          <div className="lg:w-80">
            <h4 className="text-lg font-semibold mb-3 text-center">
              📺 光屏成像效果
              {observationDirection === "right-to-left"
                ? " (从右往左看)"
                : " (从左往右看)"}
            </h4>

            {/* 亮度调节控制 */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">
                  亮度调节:
                </label>
                <input
                  type="range"
                  min="0.2"
                  max="15"
                  step="0.2"
                  value={brightnessMultiplier[0]}
                  onChange={(e) =>
                    setBrightnessMultiplier([parseFloat(e.target.value)])
                  }
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-sm font-medium text-gray-700 min-w-12">
                  {brightnessMultiplier[0]}x
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-500">
                💡 调到最低(0.2x)时几乎看不见，模拟真实小孔成像的微弱光线
              </div>
            </div>

            <div className="bg-black p-4 rounded-lg shadow-lg">
              <div className="text-white text-sm text-center mt-3 space-y-1">
                <p>
                  <strong>🔍 光斑大小:</strong>{" "}
                  {selectedPoints.length > 0
                    ? calculateLightingParams(
                        selectedPoints[0].x,
                        selectedPoints[0].y
                      ).spotSize.toFixed(1)
                    : calculateLightingParams().spotSize.toFixed(1)}
                  px
                </p>
                <p>
                  <strong>🔧 小孔形状:</strong>{" "}
                  {apertureShape === "circle"
                    ? "⭕ 圆形"
                    : apertureShape === "square"
                    ? "⬜ 方形"
                    : "🔺 三角形"}
                </p>
                <p>
                  <strong>👁️ 观察视角:</strong>{" "}
                  {observationDirection === "right-to-left"
                    ? "从右往左看"
                    : "从左往右看"}
                </p>
                <p>
                  <strong>🌟 光通量:</strong>{" "}
                  {calculateLightingParams().lightFlux.toFixed(0)}
                </p>
                <p>
                  <strong>📐 清晰度:</strong>{" "}
                  {apertureSize[0] < 15
                    ? "高"
                    : apertureSize[0] < 35
                    ? "中"
                    : "低"}
                </p>
                <p
                  className={
                    observationDirection === "right-to-left"
                      ? "text-yellow-300"
                      : "text-green-300"
                  }
                >
                  <strong>🔄 成像效果:</strong>{" "}
                  {observationDirection === "right-to-left"
                    ? "完全倒立（上下+左右颠倒）"
                    : "只有上下颠倒"}
                </p>
                {showFeaturePoints && selectedPoints.length > 0 && (
                  <p className="text-blue-300">
                    🎯 {selectedPoints.length}个特征点
                  </p>
                )}
              </div>
              {/* 画布 */}
              <div className="flex justify-center">
                <canvas
                  ref={screenViewRef}
                  className="border-2 border-gray-400 rounded"
                  style={{ imageRendering: "pixelated" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 实验指南 */}
      <div className="mt-6 bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg">
        <h3 className="font-semibold text-purple-800 mb-2">
          🧪 字母F成像实验指南（增强观察视角版）
        </h3>
        <div className="text-sm text-purple-700 space-y-2">
          <p>
            <strong>第1步:</strong> 在光路追踪图上方调节小孔直径、形状和观察视角
          </p>
          <p>
            <strong>第2步:</strong>{" "}
            点击字母F的不同颜色部位（红色竖线、蓝色顶线、绿色中线、彩色装饰点）
          </p>
          <p>
            <strong>第3步:</strong>{" "}
            使用右侧光屏成像效果上方的亮度滑块调节观察亮度
          </p>
          <p>
            <strong>第4步:</strong> 比较两种观察视角的不同效果
          </p>
          <p>
            <strong>第5步:</strong> 勾选"显示完整字母F图像"查看整个F的成像
          </p>
          <p>
            <strong>第6步:</strong> 切换不同小孔形状，观察光斑形状如何变化
          </p>
          <p>
            <strong>🔄 观察视角的重要发现:</strong>
          </p>
          <ul className="ml-6 list-disc">
            <li>
              <strong>从右往左看:</strong> 字母F完全倒立（上下+左右颠倒）
            </li>
            <li>
              <strong>从左往右看:</strong>{" "}
              由于观察者视角旋转180°，只有上下颠倒！
            </li>
          </ul>
          <p>
            <strong>🔧 形状原理:</strong>{" "}
            小孔是什么形状，光斑就是什么形状！光线直线传播形成相似投影
          </p>
          <p>
            <strong>🎯 核心物理原理:</strong> 光沿直线传播
          </p>
        </div>
      </div>

      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default PinholeCameraDemo;
